import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareButton from "@/components/ShareButton";
import { QrCode, Download, Printer, Sparkles, Store, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Marketing Kit — one page every seller can hand to their marketing team.
 *
 *   1. Personal referral link + QR      (drives NVN referral bonuses)
 *   2. Universal Share buttons          (drives the 10-share milestone)
 *   3. Printable flyer / poster         (PDF-print via window.print + @media print CSS)
 *   4. Social-media caption templates   (copy-and-paste ready for WhatsApp/IG/FB)
 *
 * Everything lives on this one page so first-time sellers can bootstrap their
 * visibility in five minutes without needing a designer.
 */

const CAPTIONS = [
  { label: "WhatsApp — short",
    text: "Hey! 👋 I just joined Find2Near — a hyperlocal super-app for shopping, services, jobs, matrimonial + more. Check it out and use my link to sign up: {URL}" },
  { label: "Instagram / Facebook — story",
    text: "🌍 New way to discover everything nearby — from tiffin services to 3BHK flats, matrimonial matches to trusted plumbers.\n\nEarn reward points on every action. Join with my link 👉 {URL}" },
  { label: "LinkedIn — professional",
    text: "If you're a small-business owner or freelancer, Find2Near is the easiest way to reach nearby customers. Zero-config listings, verified trust badges, referral rewards. Try it here: {URL}" },
  { label: "SMS — quick",
    text: "Found something you'll love — Find2Near. Sign up here and I get a small reward too 🙏 {URL}" },
];

export default function MarketingKit() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [nvn, setNvn] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/rewards/summary").then(r => setSummary(r.data)).catch(() => {});
    api.get(`/wuid/me`).then(r => setNvn(r.data?.nvn || null)).catch(() => {});
  }, [user]);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://find2near.com";
  // Use the caller's NVN as the referral code so ?ref= attaches during signup.
  const refCode = nvn || user?.user_id || "friend";
  const refUrl = `${origin}/?ref=${encodeURIComponent(refCode)}`;
  const qrUrl = `${API}/qr/generate?text=${encodeURIComponent(refUrl)}&size=280`;

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text.replace(/\{URL\}/g, refUrl));
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch { toast.error("Copy failed"); }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="heading text-3xl font-extrabold text-stone-900">Marketing Kit</h1>
        <p className="mt-3 text-stone-600">Log in to see your personal referral link, QR code, and printable flyer.</p>
        <Link to="/login" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#E05E36] text-white font-bold">Log in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 print:py-2" data-testid="marketing-kit-page">
      {/* Print-hidden nav */}
      <div className="print:hidden">
        <div className="text-xs uppercase tracking-widest font-bold text-[#8B5E34] flex items-center gap-1.5"><Sparkles size={12}/> Marketing Kit</div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mt-1">Your kit to grow, in five minutes</h1>
        <p className="text-stone-600 mt-2">Share your link, print the flyer, post the caption. Every unique share earns you <b>+1 pt</b> (+ <b>100 pt</b> bonus at 10). Every referred signup earns <b>+10 pt</b> (+ <b>100 pt</b> bonus at 10).</p>
      </div>

      {/* ROW 1 — Referral link + QR + Share buttons */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-stone-500 mb-2"><QrCode size={13}/> Your referral link</div>
          <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
            <div className="text-sm text-stone-800 truncate flex-1 font-mono">{refUrl}</div>
            <button
              onClick={() => copy("ref", refUrl)}
              className="text-xs font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a] rounded-full px-3 py-1.5 flex items-center gap-1.5"
              data-testid="copy-ref-link"
            >
              {copiedKey === "ref" ? <Check size={13}/> : <Copy size={13}/>} Copy
            </button>
          </div>
          <div className="mt-4 flex gap-3 items-center">
            <ShareButton url={refUrl} title="Join me on Find2Near" text="Come join Find2Near — everything nearby, all in one place." testIdSuffix="marketing-kit" variant="primary" />
            {summary && (
              <span className="text-xs text-stone-600">
                <b>{summary.balance_pts}</b> pts · <b>{summary.share.count}/{summary.share.target}</b> shares · <b>{summary.referral.count}/{summary.referral.target}</b> referrals
              </span>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6 grid place-items-center print:break-inside-avoid">
          <img loading="lazy" src={qrUrl} alt="Your referral QR" className="w-52 h-52 md:w-60 md:h-60 object-contain" data-testid="referral-qr" />
          <div className="mt-3 text-center">
            <div className="font-bold text-stone-900">Scan to join Find2Near</div>
            <div className="text-xs text-stone-500">Ref: {refCode}</div>
          </div>
          <a href={qrUrl} download={`find2near-qr-${refCode}.svg`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#E05E36] hover:underline print:hidden">
            <Download size={12}/> Download QR
          </a>
        </div>
      </div>

      {/* ROW 2 — Social captions */}
      <div className="mt-8 print:hidden">
        <h2 className="heading text-xl md:text-2xl font-extrabold text-stone-900">Ready-to-post captions</h2>
        <p className="text-stone-600 text-sm mt-1">Tap Copy — your link is already substituted in.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {CAPTIONS.map((c, i) => (
            <div key={i} className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{c.label}</div>
              <p className="mt-2 text-sm text-stone-800 whitespace-pre-line">{c.text.replace("{URL}", refUrl)}</p>
              <button
                onClick={() => copy(`cap-${i}`, c.text)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-800 hover:border-[#E05E36] hover:text-[#E05E36]"
                data-testid={`copy-caption-${i}`}
              >
                {copiedKey === `cap-${i}` ? <Check size={13} className="text-emerald-600"/> : <Copy size={13}/>} Copy
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ROW 3 — Printable flyer (only visible section on print) */}
      <div className="mt-8">
        <div className="flex items-center justify-between print:hidden">
          <h2 className="heading text-xl md:text-2xl font-extrabold text-stone-900">Printable flyer</h2>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white px-4 py-2 text-sm font-bold"
            data-testid="print-flyer"
          >
            <Printer size={14}/> Print
          </button>
        </div>

        <div className="mt-4 rounded-3xl border-2 border-dashed border-stone-300 bg-white p-6 md:p-10 print:border-0 print:p-0 print:break-before-page" data-testid="flyer">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#E05E36]"><Store size={13}/> Everything Nearby</div>
            <h1 className="heading text-5xl md:text-6xl font-black text-stone-900 mt-2 leading-tight">Find2Near</h1>
            <p className="text-stone-700 mt-3 text-lg">Shop · Rent · Book · Hire · Match — every service, every citizen, in one app.</p>
            <img loading="lazy" src={qrUrl} alt="QR" className="w-56 h-56 mx-auto mt-6" />
            <div className="mt-3 font-mono text-sm text-stone-700">{refUrl}</div>
            <div className="mt-1 text-xs text-stone-500">Scan the QR or open the link · Ref: {refCode}</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          nav, footer, [data-testid="voice-mic-root"], .print\\:hidden { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
